import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Style from './CommandK.module.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { info } from '../../info/Info';

const SUGGESTIONS = [
    'help',
    'whoami',
    'GET /projects',
    'GET /experience',
    'GET /skills',
    'GET /resume',
    'POST /contact',
    'sudo hire-me',
];

const ok = (text) => ({ text, tone: 'ok' });
const err = (text) => ({ text, tone: 'err' });
const dim = (text) => ({ text, tone: 'dim' });
const txt = (text) => ({ text, tone: 'txt' });

function runCommand(raw) {
    const cmd = raw.trim().replace(/\s+/g, ' ');
    const lower = cmd.toLowerCase();

    if (lower === 'help') {
        return [
            dim('available endpoints:'),
            txt('  GET /projects          list shipped work'),
            txt('  GET /projects/<slug>   one project, in detail'),
            txt('  GET /experience        where I\'ve shipped'),
            txt('  GET /skills            the toolbox'),
            txt('  GET /resume            request a copy by email'),
            txt('  GET /contact           ways to reach me'),
            txt('  POST /contact          send me a message'),
            txt('  whoami                 identity check'),
            txt('  sudo hire-me           elevated privileges'),
            txt('  clear                  wipe the session'),
        ];
    }

    if (lower === 'whoami') {
        return [
            ok('200 OK'),
            txt('{'),
            txt(`  "name":     "${info.firstName} ${info.lastName}",`),
            txt(`  "role":     "${info.position}",`),
            txt(`  "company":  "${info.company}",`),
            txt(`  "location": "${info.location}",`),
            txt('  "mission":  "I build Agentic AI Systems"'),
            txt('}'),
        ];
    }

    if (lower === 'get /projects' || lower === 'get /work') {
        return [
            ok(`200 OK · ${info.portfolio.length} results`),
            ...info.portfolio.map((p) =>
                txt(`  /projects/${p.slug.padEnd(24)} ${p.badge}`)
            ),
            dim('hint: GET /projects/cognivox-ai'),
        ];
    }

    const projMatch = lower.match(/^get \/projects\/([\w-]+)$/);
    if (projMatch) {
        const p = info.portfolio.find((x) => x.slug === projMatch[1]);
        if (!p) {
            return [
                err('404 Not Found'),
                dim(`no project at /projects/${projMatch[1]} — try GET /projects`),
            ];
        }
        return [
            ok(`200 OK · ${p.ms}`),
            txt('{'),
            txt(`  "title":  "${p.title}",`),
            txt(`  "status": "${p.live ? 'live' : 'source available'}",`),
            txt(`  "stack":  [${p.tech.map((t) => `"${t}"`).join(', ')}],`),
            txt(`  "about":  "${p.description}"`),
            txt('}'),
            dim(`→ ${p.source}`),
        ];
    }

    if (lower === 'get /experience') {
        return [
            ok(`200 OK · ${info.experience.length} spans`),
            ...info.experience.flatMap((job) => [
                txt(`  ${job.index}  ${job.company} — ${job.role}`),
                dim(`      ${job.current ? '● ' : ''}${job.period} · ${job.stack.join(' · ')}`),
            ]),
        ];
    }

    if (lower === 'get /skills') {
        return [
            ok('200 OK'),
            ...info.skills.map((g) => txt(`  ~/${g.group.padEnd(10)} ${g.items.join(', ')}`)),
        ];
    }

    if (
        lower === 'get /resume' || lower === 'get /cv' ||
        lower === 'post /resume' || lower === '/resume' || lower === '/cv' ||
        lower === 'resume' || lower === 'cv'
    ) {
        return [
            ok('202 Accepted · request logged'),
            txt('  got your request — I\'ll send my resume over myself.'),
            dim('  the PDF isn\'t published anywhere; it goes out by email only.'),
            dim('  tell me where to send it and what it\'s for ↓'),
            { text: 'resume', tone: 'resume' },
        ];
    }

    if (lower === 'get /contact') {
        return [
            ok('200 OK'),
            ...info.socials.map((s) => txt(`  ${s.label.toLowerCase().padEnd(11)} ${s.link}`)),
            dim('hint: sudo hire-me'),
        ];
    }

    if (lower === 'sudo hire-me' || lower === 'sudo hire me') {
        return [
            ok('ACCESS GRANTED'),
            txt('{'),
            txt('  "status":  "open to interesting problems",'),
            txt('  "action":  "opening compose…"'),
            txt('}'),
            { text: 'compose', tone: 'contact' },
        ];
    }

    if (lower === 'post /contact' || lower === 'contact' || lower === 'email' || lower === 'message') {
        return [
            ok('opening compose…'),
            dim('write me a message — your email is never shown to anyone but me'),
            { text: 'compose', tone: 'contact' },
        ];
    }

    return [
        err('404 Not Found'),
        dim(`unknown command: ${cmd} — try help`),
    ];
}

export default function CommandK({ open, onClose, onOpenContact, onOpenResume }) {
    const [history, setHistory] = useState([]);
    const [value, setValue] = useState('');
    const [lastCmd, setLastCmd] = useState('');
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const timerRef = useRef(null);

    const suggestions = useMemo(() => {
        const v = value.trim().toLowerCase();
        if (!v) return SUGGESTIONS;
        return SUGGESTIONS.filter((s) => s.toLowerCase().startsWith(v) && s.toLowerCase() !== v);
    }, [value]);

    const submit = useCallback((raw) => {
        const cmd = raw.trim();
        if (!cmd) return;
        setValue('');
        setLastCmd(cmd);

        if (cmd.toLowerCase() === 'clear') {
            setHistory([]);
            return;
        }

        const lines = runCommand(cmd);
        const id = Date.now();
        setHistory((h) => [...h, { id, cmd, lines, shown: 0 }]);

        let count = 0;
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            count += 1;
            setHistory((h) =>
                h.map((entry) => (entry.id === id ? { ...entry, shown: count } : entry))
            );
            if (count >= lines.length) {
                clearInterval(timerRef.current);
                const link = lines.find((l) => l.tone === 'open');
                if (link) setTimeout(() => { window.open(link.text, '_blank', 'noopener'); }, 700);
                const wantsContact = lines.some((l) => l.tone === 'contact');
                if (wantsContact && onOpenContact) setTimeout(() => onOpenContact(), 650);
                const wantsResume = lines.some((l) => l.tone === 'resume');
                if (wantsResume && onOpenResume) setTimeout(() => onOpenResume(), 650);
            }
        }, 55);
    }, [onOpenContact, onOpenResume]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 60);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, [history]);

    useEffect(() => () => clearInterval(timerRef.current), []);

    const onKeyDown = (e) => {
        if (e.key === 'Enter') submit(value);
        if (e.key === 'Tab') {
            e.preventDefault();
            if (suggestions.length) setValue(suggestions[0]);
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setValue(lastCmd);
        }
        if (e.key === 'Escape') onClose();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className={Style.backdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        className={Style.panel}
                        role="dialog"
                        aria-modal="true"
                        aria-label="portfolio terminal"
                        initial={{ opacity: 0, y: 18, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.99 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className={Style.bar}>
                            <span className={Style.dot} />
                            sai@portfolio — interactive
                            <button className={Style.esc} onClick={onClose}>esc</button>
                        </div>

                        <div className={Style.scroll} ref={scrollRef}>
                            {history.length === 0 && (
                                <p className={Style.hintLine}>
                                    this portfolio has an API. try <em>help</em>, or click a command below.
                                </p>
                            )}
                            {history.map((entry) => (
                                <div key={entry.id} className={Style.entry}>
                                    <p className={Style.cmdLine}>
                                        <span className={Style.prompt}>$</span> {entry.cmd}
                                    </p>
                                    {entry.lines.slice(0, entry.shown).map((line, i) =>
                                        (line.tone === 'open' || line.tone === 'contact' || line.tone === 'resume') ? null : (
                                            <p key={i} className={Style[line.tone]}>{line.text}</p>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className={Style.inputRow}>
                            <span className={Style.prompt}>$</span>
                            <input
                                ref={inputRef}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="type a command — tab autocompletes"
                                spellCheck={false}
                                autoCapitalize="none"
                                autoCorrect="off"
                            />
                        </div>

                        <div className={Style.chips}>
                            {suggestions.map((s) => (
                                <button key={s} onClick={() => submit(s)}>{s}</button>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
